import { GeneralCsvReportEntity } from "domain/entity/GeneralCsvReport/GeneralCsvReportEntity";
import { RemoteFileInfoRepository } from "domain/repository/Common/RemoteFileInfoRepo";
import { CompanyRepository } from "domain/repository/Company/CompanyRepo";
import { GeneralCsvReportRepository } from "domain/repository/GeneralCsvReport/GeneralCsvReportRepo";
import _ from "lodash";
import moment from "moment";
import { AllNbisivPermission } from "presentation/constant/ANAInfo/NbisivPermission";
import { generatePermissionByRptId, ReportView } from "presentation/constant/ANAInfo/ReportPermission";
import { CustomerTypeDroOpts } from "presentation/constant/DropDownOptions/Company/CustomerTypeDroOpts";
import { GeneralCsvReportModel } from "presentation/model/GeneralCsvReport/GeneralCsvReportModel";
import { Dispatch, SetStateAction } from "react";
import { IFieldValue } from "veronica-ui-component/dist/component/core";
import BaseViewModel from "../BaseViewModel";

interface GeneralCsvReportVMProps extends BaseViewModel {
    dispatch: [
        Dispatch<SetStateAction<GeneralCsvReportModel>> | ((value: SetStateAction<GeneralCsvReportModel>) => void),
    ],
    generalCsvReportRepo: GeneralCsvReportRepository,
    remoteFileInfoRepo: RemoteFileInfoRepository,
    companyRepo: CompanyRepository,
}

export const GeneralCsvReportVM = ({ dispatch, generalCsvReportRepo, remoteFileInfoRepo, companyRepo }: GeneralCsvReportVMProps) => {
    const [generalCsvReportDispatch] = dispatch;

    const loadDropdownOption = async () => {

        await companyRepo.getAllCompanyForCombobox().then(
            companyList => {
                const opsLines = companyList.filter(entity => entity.customerType === 'SL');
                let opsLinesDropdownOptions = opsLines?.map((opsLineEntity) => ({
                    dropdownLabel: opsLineEntity.companyCode,
                    tagLabel: opsLineEntity.companyCode,
                    value: opsLineEntity.companyCode,
                })) ?? []
                opsLinesDropdownOptions = _.orderBy(opsLinesDropdownOptions, "dropdownLabel");
                generalCsvReportDispatch(prevState => ({
                    ...prevState,
                    dynamicOptions: {
                        ...prevState.dynamicOptions,
                        opsLineDropdownOptions: opsLinesDropdownOptions
                    }
                }))
            }
        )

        await remoteFileInfoRepo.getFileLoc().then(
            fileLocs => {
                let fileLocationDropdownOptions = fileLocs?.map((fileLoc) => ({
                    dropdownLabel: fileLoc,
                    tagLabel: fileLoc,
                    value: fileLoc,
                })) ?? []
                fileLocationDropdownOptions = _.orderBy(fileLocationDropdownOptions, "dropdownLabel");

                generalCsvReportDispatch(prevState => ({
                    ...prevState,
                    dynamicOptions: {
                        ...prevState.dynamicOptions,
                        fileLocationDropdownOptions: fileLocationDropdownOptions,
                    },
                    currentReport: {
                        ...prevState.currentReport,
                        fileLocation: fileLocationDropdownOptions.length > 0 ? fileLocationDropdownOptions[0].value : '',
                    },
                }))
            }
        )

        generalCsvReportDispatch(prevState => ({
            ...prevState,
            dynamicOptions: {
                ...prevState.dynamicOptions,
                customerTypeDropdownOptions: CustomerTypeDroOpts().getCustomerTypeModelForOutputVatReport(),
            }
        }))
    }

    const initGeneralCsvReportList = async (permissions: AllNbisivPermission) => {
        await generalCsvReportRepo.initGeneralCsvReportList().then(data => {
            const [reportIds, reportNames] = data.reduce<[string[], string[]]>((acc, item) => {
                const [key, value] = item.split(':', 2);

                const rptPermission = generatePermissionByRptId(ReportView.GENERAL_CSV_REPORT, key)
                const permission = permissions[rptPermission];

                if (permission && permission.acl && permission.acl.includes('R')) {
                    acc[0].push(key);
                    acc[1].push(value.trim());
                }
                return acc;
            }, [[], []] as [string[], string[]]);

            const dataObj = data.reduce((acc, item) => {
                const [key, value] = item.split(':', 2);
                acc[value] = key.trim();
                return acc;
            }, {} as { [key: string]: string });

            const reportTypeDropdownOption = reportNames?.map((report) => ({
                dropdownLabel: report,
                tagLabel: report,
                value: report,
            })) ?? []

            const now = moment();
            const year = now.year();
            const month = now.format('MM');

            generalCsvReportDispatch(prevState => {
                return {
                    ...prevState,
                    reportIdTypeMap: dataObj,
                    reportTypeList: reportNames,
                    reportIdList: reportIds,
                    dynamicOptions: {
                        ...prevState.dynamicOptions,
                        reportTypeDropdownOptions: reportTypeDropdownOption
                    },
                    currentReport: {
                        ...prevState.currentReport,
                        reportType: reportTypeDropdownOption.length > 0 ? reportTypeDropdownOption[0].value : '',
                        opsLine: prevState.dynamicOptions.opsLineDropdownOptions.length > 0 ? prevState.dynamicOptions.opsLineDropdownOptions[0].value : '',
                        year: year.toString(),
                        month: month.toString(),
                    }
                }
            })
        })
    }

    const onHeaderFieldChange = async (fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) => {
        let val: any = fieldValue;
        /*if (fieldKey === 'reportType') {
            onReportTypeChanged(val);
        }*/
        if (fieldKey === 'year') {
            if (val === null || val === '') {
                val = "";
            } else {
                val = val.replace(/[^0-9]/g, '');
            }

        }
        generalCsvReportDispatch(prevState => {
            return {
                ...prevState,
                currentReport: {
                    ...prevState.currentReport,
                    [fieldKey]: val,
                },
            }
        })
    }

    const onReportTypeChanged = async (fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) => {
        let reportType: any = fieldValue;

        const now = moment();
        const year = now.year();
        const month = now.format('MM');
        let flag = false;
        if (month === "12" && reportType === "Free Pool Volume") {
            flag = true;
        }
        generalCsvReportDispatch(prevState => ({
            ...prevState,
            currentReport: {
                ...prevState.currentReport,
                reportType: reportType,
                year: flag ? moment().subtract(1, 'year').year().toString() : year.toString(),
                opsLine: prevState.dynamicOptions.opsLineDropdownOptions.length > 0 ? prevState.dynamicOptions.opsLineDropdownOptions[0].value : '',
                month: reportType === "Free Pool Volume" ? moment().subtract(1, 'month').format('MM').toString() : month.toString(),
                includeNonVat: "Y",
                docDateFrom: null,
                docDateTo: null
            },
        }))
    }

    const onResetClick = async () => {
        const now = moment();
        const year = now.year();
        const month = now.format('MM');
        generalCsvReportDispatch(prevState => ({
            ...prevState,
            currentReport: {
                ...prevState.currentReport,
                reportType: prevState.dynamicOptions.reportTypeDropdownOptions.length > 0 ? prevState.dynamicOptions.reportTypeDropdownOptions[0].value : '',
                fileLocation: prevState.dynamicOptions.fileLocationDropdownOptions.length > 0 ? prevState.dynamicOptions.fileLocationDropdownOptions[0].value : '',
                opsLine: prevState.dynamicOptions.opsLineDropdownOptions.length > 0 ? prevState.dynamicOptions.opsLineDropdownOptions[0].value : '',
                month: month.toString(),
                year: year.toString(),
                includeNonVat: "Y",
                docDateFrom: null,
                docDateTo: null,
                customerType: null,
            },
        }))
    }

    const onDownloadClick = async (reportIdTypeMap: { [key: string]: string }, entity: GeneralCsvReportEntity, downloadType?: string) => {
        let data = { ...entity };
        data = {
            ...data,
            reportType: reportIdTypeMap[entity.reportType],
            exportType: downloadType,
        }
        return await generalCsvReportRepo.download(data).then((data) => {
            if (data && data.success) {
                const res = data.data;
                for (const key in res) {
                    if (res[key]) {
                        const tempData = res[key] as string;
                        let base64 = tempData;
                        let bstr = atob(base64);
                        let n = bstr.length;
                        let u8arr = new Uint8Array(n);
                        while (n--) {
                            u8arr[n] = bstr.charCodeAt(n)
                        }
                        if (downloadType) {
                            const blob = new Blob([u8arr], { type: 'text/csv' });
                            const url = URL.createObjectURL(blob);
                            const a = document.createElement('a');
                            a.href = url;
                            a.download = key + "." + downloadType.toLowerCase();
                            a.style.display = 'none';
                            document.body.appendChild(a);
                            a.click();
                            window.URL.revokeObjectURL(url);
                            document.body.removeChild(a);
                        }
                    }
                }
                return "success";
            } else {
                return data.data ?? "Download Failed.";

            }
        })
    }


    const onExportClick = async (exportType: string, reportIdTypeMap: { [key: string]: string }, entity: GeneralCsvReportEntity) => {
        let data = { ...entity };
        data = {
            ...data,
            reportType: reportIdTypeMap[entity.reportType],
            exportType: exportType,
        }
        return await generalCsvReportRepo.exportReport(data);
    }

    const onCheckboxChange = (checked: boolean, fieldName: string) => {
        generalCsvReportDispatch(prevState => {
            return {
                ...prevState,
                currentReport: {
                    ...prevState.currentReport,
                    [fieldName]: checked ? "Y" : "N",
                },
            }
        })
    }

    return {
        onCheckboxChange: onCheckboxChange,
        onExportClick: onExportClick,
        onDownloadClick: onDownloadClick,
        onResetClick: onResetClick,
        onHeaderFieldChange: onHeaderFieldChange,
        loadDropdownOption: loadDropdownOption,
        initGeneralCsvReportList: initGeneralCsvReportList,
        onReportTypeChanged: onReportTypeChanged
    }
}